import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../api';

/**
 * BD-11 — the Activity Feed: one timeline of meetings, debriefs, approvals, Zoho
 * writes, Outlook drafts, Jarvis PM tasks and notes, replies and follow-ups
 * (GET /api/bd/activity). Everyone sees their own; an admin sees everyone's and
 * can pick a person. Filterable by account and contact. Reads the BD tables only.
 */

interface Item {
  id: string;
  kind: string;
  at: string;
  title: string;
  detail?: string | null;
  user_id: number | null;
  person_ids?: number[];
  link?: string;
}
interface Facet { id: number; name: string }
interface FeedResponse {
  items: Item[];
  facets: { people: Facet[]; accounts: Facet[]; users: Facet[] };
}

const KIND_LABEL: Record<string, string> = {
  meeting: 'Meeting',
  debrief: 'Debrief',
  zoho_write: 'Zoho',
  email_drafted: 'Email draft',
  task: 'Task',
  pm_note: 'PM note',
  proposed: 'Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  conflict: 'Conflict',
  outcome: 'Reply / meeting',
  reminder_set: 'Follow-up',
  reminder_closed: 'Follow-up done',
  interaction: 'Interaction',
};

const SELECT_CLS = 'text-sm border border-gray-300 rounded-lg px-2 py-1.5 text-gray-700';
const dayOf = (at: string) => new Date(at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const timeOf = (at: string) => new Date(at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

const ActivityFeedPanel: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const [days, setDays] = useState(30);
  const [orgId, setOrgId] = useState('');
  const [personId, setPersonId] = useState('');
  const [userId, setUserId] = useState('');
  const [data, setData] = useState<FeedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // Only the newest request may update the list: a slow answer for an earlier
  // filter must not overwrite the one the user picked last.
  const latest = useRef(0);
  const load = useCallback(async () => {
    const seq = ++latest.current;
    setLoading(true);
    setFailed(false);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (orgId) params.set('org_id', orgId);
      if (personId) params.set('person_id', personId);
      if (isAdmin && userId) params.set('user_id', userId);
      const { data: res } = await api.get(`/api/bd/activity?${params.toString()}`);
      if (seq === latest.current) setData(res);
    } catch {
      if (seq === latest.current) setFailed(true);
    } finally {
      if (seq === latest.current) setLoading(false);
    }
  }, [days, orgId, personId, userId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const groups: { day: string; items: Item[] }[] = [];
  for (const it of items) {
    const day = dayOf(it.at);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(it);
    else groups.push({ day, items: [it] });
  }
  const userName = (id: number | null) => data?.facets.users.find((u) => u.id === id)?.name;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5" data-testid="bd-activity-feed">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Activity</h3>
          <p className="text-xs text-gray-500">
            {isAdmin ? 'Everyone’s BD activity. Meetings and debriefs show for their owner only.' : 'Your BD activity.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {loading && data && (
            <span className="flex items-center gap-1 text-xs text-gray-500" data-testid="bd-activity-refreshing"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Updating…</span>
          )}
          <select aria-label="Period" className={SELECT_CLS} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="bd-activity-days">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <select aria-label="Account" className={SELECT_CLS} value={orgId} onChange={(e) => setOrgId(e.target.value)} data-testid="bd-activity-account">
            <option value="">All accounts</option>
            {(data?.facets.accounts || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <select aria-label="Contact" className={SELECT_CLS} value={personId} onChange={(e) => setPersonId(e.target.value)} data-testid="bd-activity-person">
            <option value="">All contacts</option>
            {(data?.facets.people || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {isAdmin && (
            <select aria-label="Person" className={SELECT_CLS} value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="bd-activity-user">
              <option value="">Everyone</option>
              {(data?.facets.users || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {loading && !data && (
        <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="bd-activity-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading activity…</div>
      )}
      {failed && (
        <p className="text-sm text-red-600" data-testid="bd-activity-error">
          The activity feed could not be loaded. <button type="button" className="underline" onClick={load}>Try again</button>
        </p>
      )}
      {!failed && data && items.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="bd-activity-empty">Nothing in this period.</p>
      )}
      {!failed && groups.length > 0 && (
        <div className={`space-y-4 transition-opacity ${loading ? 'opacity-50' : ''}`} aria-busy={loading} data-testid="bd-activity-list">
          {groups.map((g) => (
            <div key={g.day}>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">{g.day}</div>
              <ul className="divide-y divide-gray-100">
                {g.items.map((it) => (
                  <li key={it.id} className="flex items-start gap-3 py-2" data-testid={`bd-activity-item-${it.kind}`}>
                    <span className="mt-0.5 shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">{KIND_LABEL[it.kind] || it.kind}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-900">
                        {it.link ? <a href={it.link} className="hover:underline">{it.title}</a> : it.title}
                      </div>
                      {(it.detail || (isAdmin && userName(it.user_id))) && (
                        <div className="text-xs text-gray-500">
                          {[it.detail, isAdmin ? userName(it.user_id) : null].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                    <span className="shrink-0 text-xs text-gray-400">{timeOf(it.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityFeedPanel;
